import axios from "axios";
import { reportPilotNetworkFailure } from "./pilotDiagnostics";

const SAFE = new Set(["get", "head", "options"]);
const tokenByRealm = new Map();

function realmForUrl(url = "") {
  const path = String(url);
  if (path.startsWith("/api/admin") || path.startsWith("/api/developer/auth") || path.startsWith("/api/agent-chat")) return "admin";
  if (path.startsWith("/api/ytl")) return "ytl";
  if (path.startsWith("/api/deploy") || path.startsWith("/api/marketplace") || path.startsWith("/api/media/merchant") || path.startsWith("/api/creator") || path.startsWith("/api/arixa") || path.startsWith("/api/limelite")) return "deploy";
  return "legacy";
}

function isSameOrigin(url = "") {
  if (String(url).startsWith("/")) return true;
  try { return new URL(url, window.location.href).origin === window.location.origin; } catch { return false; }
}

async function csrfToken(realm) {
  if (!tokenByRealm.has(realm)) {
    const pending = fetch(`/api/${realm === "legacy" ? "auth" : realm}/csrf`, { credentials: "include", cache: "no-store" })
      .then(async (response) => {
        if (!response.ok) return "";
        const body = await response.json();
        return typeof body.csrfToken === "string" ? body.csrfToken : "";
      })
      .catch(() => "");
    tokenByRealm.set(realm, pending);
    pending.then((token) => { if (!token) tokenByRealm.delete(realm); });
  }
  return tokenByRealm.get(realm);
}

function attach(instance) {
  instance.interceptors.request.use(async (config) => {
    const method = String(config.method || "get").toLowerCase();
    if (SAFE.has(method) || !isSameOrigin(config.url)) return config;
    const token = await csrfToken(realmForUrl(config.url));
    if (token) {
      if (config.headers?.set) config.headers.set("X-CSRF-Token", token);
      else config.headers = { ...(config.headers || {}), "X-CSRF-Token": token };
    }
    return config;
  });
  instance.interceptors.response.use((response) => {
    const path = String(response.config?.url || "");
    const method = String(response.config?.method || "get").toLowerCase();
    // MFA/OTP confirmation regenerates the admin session too, so clearing only login/logout
    // would leave the pending-session token cached and make the first real admin action 403.
    if (!SAFE.has(method) && /^\/api\/(?:deploy\/auth|developer\/auth|auth|ytl\/auth)\//.test(path) && response.data?.success) {
      tokenByRealm.delete(realmForUrl(path));
    }
    return response;
  }, (error) => {
    reportPilotNetworkFailure(error);
    return Promise.reject(error);
  });
  return instance;
}

async function csrfFetch(url, init = {}) {
  const method = String(init.method || "GET").toLowerCase();
  if (SAFE.has(method) || !isSameOrigin(url)) return fetch(url, init);
  const token = await csrfToken(realmForUrl(url));
  return fetch(url, { ...init, headers: { ...(init.headers || {}), ...(token ? { "X-CSRF-Token": token } : {}) } });
}

// Side effect imported before App: patch both the singleton and every axios.create client before
// page modules initialise. No response interceptor means a failed mutation is never replayed.
const originalCreate = axios.create.bind(axios);
axios.create = (...args) => attach(originalCreate(...args));
attach(axios);

export { realmForUrl, isSameOrigin, csrfFetch };
